import { allDayDragHelperTemplate, dayWeekDragHelperTemplate, makeDraggable } from './Templates';
import { CalendarDayContent, SurfaceConstants } from 'owa-calendar-surface-core';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { OwaDate } from 'owa-datetime';
import { getGuid } from 'owa-guid';
import * as React from 'react';
import {
    CalendarSurfaceStore,
    CalendarSurfaceTransformProps,
    allDayPanel,
    createGridLines,
    createHeaders,
    ColumnItemsProvider,
    SurfaceColumnItemsProvider,
    createColumnsWithContent,
    createConflictGroupsLayer,
    transformAllDayItem,
    SurfaceItem,
    generateOverflowItems,
    TimeStripContent,
    TimeStripHeaderContent,
    TimeMarkerContent,
    TimeMarkerTodayContent,
} from 'owa-calendar-surface-dategrid';
import {
    getAllowedResizeTypesForRegularItem,
    getAllowedResizeTypesForAllDayItem,
} from './store/calculation/getAllowedResizeTypes';
import AllowedResizeTypes from './store/schema/AllowedResizeTypes';
import { getAllDayWellHeight } from 'owa-calendar-surface-options-utils';
import CalendarAllDayColumnComponent from './components/CalendarAllDayColumnComponent';
import CalendarInDayColumnComponent from './components/CalendarInDayColumnComponent';
import WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';

/**
 * Creates content for mounting in calendar day (week) view
 * @param store the calendar surface store
 * @param config the configuration for calendar day/week view
 * @param disableDateHeader the check to remove date column header from calendar
 * @param calendarWorkingHours working hours for the particular calendar folder
 * @returns structured content for day/week view
 */
export function createCalendarDayContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    getItems: () => CalendarEvent[],
    calendarFolderId?: string,
    customEventsComparator?: (item1: CalendarEvent, item2: CalendarEvent) => number,
    disableDateHeader?: boolean,
    customTimeMarkerClassName?: string,
    calendarWorkingHours?: WorkingHoursType,
    hideTodayEmphasis?: boolean
): CalendarDayContent {
    let columnItemsProvider = new SurfaceColumnItemsProvider(
        store,
        config.columnProvider,
        getItems,
        config.supportExpandableAllDayWell(),
        undefined,
        customEventsComparator,
        getAllDayWellHeight()
    );

    return {
        gridLines: () => createGridLines(store, config),
        headers: (hideHeaderAddOns: boolean) =>
            disableDateHeader
                ? null
                : createHeaders(
                      store,
                      config,
                      hideHeaderAddOns,
                      calendarFolderId,
                      hideTodayEmphasis
                  ),
        content: () =>
            createCalendarInDayContent(
                columnItemsProvider,
                store,
                config,
                calendarFolderId,
                customTimeMarkerClassName,
                calendarWorkingHours
            ),
        timeStripContent: () => createCalendarTimeStripContent(store, config),
        timeStripHeaderContent: () => createCalendarTimeStripHeaderContent(store, config),
        allDayContent: () =>
            createAllDayPanel(columnItemsProvider, store, config, calendarFolderId),
        tabOrderProvider: () => columnItemsProvider.tabOrder,
        allDayContentProperties: () => ({
            visibleRowCount: columnItemsProvider.visibleRowCount,
            allDayUncollapsedRowCount: columnItemsProvider.allDayUncollapsedRows.length,
            allDayRowCount: columnItemsProvider.allDayRows.length,
        }),
    };
}

/**
 * Creates the calendar surface content
 * @param items surface items collection
 * @param allDayModels collection of all day items
 * @param store surface store
 * @param config the configuration for calendar day/week view
 * @param calendarWorkingHours working hours for the particular calendar folder
 * @returns collection JSX elements of surface items
 */
export function createCalendarInDayContent(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    calendarFolderId?: string,
    customTimeMarkerClassName?: string,
    calendarWorkingHours?: WorkingHoursType
): JSX.Element[] {
    let makeDraggableCallback = (element, groupIndex, item) => {
        let allowedResizeTypes = getAllowedResizeTypesForRegularItem(
            store,
            config.dragDropConfig,
            item.model,
            config.columnProvider.getColumnDate.bind(config.columnProvider),
            groupIndex
        );
        return makeDraggable(store, config.dragDropConfig, item, element, allowedResizeTypes);
    };
    let columnElements = createColumnsWithContent(
        config.columnProvider,
        (columnModel, index) => {
            return createConflictGroupsLayer(
                cip.columnInDayItems[index],
                store,
                config,
                index,
                makeDraggableCallback
            );
        },
        (columnModel, index) => {
            const busyModelsProvider = () => cip.busyStripModels[index];
            return {
                component: CalendarInDayColumnComponent,
                additionalProps: {
                    store: store,
                    config: config,
                    calendarFolderId: calendarFolderId,
                    date: columnModel as OwaDate,
                    busyModelsProvider: busyModelsProvider,
                    index: index,
                    calendarWorkingHours: calendarWorkingHours,
                },
            };
        }
    );

    if (store.enableDragDrop) {
        columnElements.push(dayWeekDragHelperTemplate(store, config, calendarFolderId));
    }

    if (config.shouldShowCurrentTime?.()) {
        columnElements.push(...createTimeMarkerContent(config, customTimeMarkerClassName));
    }

    return columnElements;
}

/**
 * Creates all day panel with content
 * @param cip The column items provider
 * @param store The calendar surface store
 * @param config The surface transform config
 */
export function createAllDayPanel(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    calendarFolderId?: string
): JSX.Element[] {
    return [
        ...createCalendarAllDayColumns(config),
        allDayPanel(getGuid(), cip, () => {
            return createCalendarAllDayContent(cip, store, config, calendarFolderId);
        }),
    ];
}

function createCalendarAllDayColumns(config: CalendarSurfaceTransformProps): JSX.Element[] {
    return createColumnsWithContent(
        config.columnProvider,
        () => null,
        columnModel => ({
            component: CalendarAllDayColumnComponent,
            additionalProps: {
                /* NOTE: assuming columnModel is an OwaDate (it happened before in ColumnPastDayHighlightBehavior itself) */
                date: columnModel as OwaDate,
            },
        })
    );
}

/**
 * Creates the content (JSX elements) for the all day well
 * @param cip The column items provider
 * @param store The surface store
 * @param config the configuration for calendar day/week view
 * @returns An array of JSX elements with all components for the all-day well
 */
function createCalendarAllDayContent(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    calendarFolderId?: string
): JSX.Element[] {
    let result: JSX.Element[] = [];

    cip.allDayRows.forEach((rowItems, rowIndex) => {
        result.push(
            ...rowItems.map(item => {
                let allowedResizeTypes = getAllowedResizeTypesForAllDayItem(
                    store,
                    config.dragDropConfig,
                    item.model
                );
                return makeDraggable(
                    store,
                    config.dragDropConfig,
                    item,
                    transformAllDayItem(
                        store,
                        config.columnProvider,
                        config.getItemConfig(item.model),
                        item,
                        rowIndex
                    ),
                    allowedResizeTypes
                );
            })
        );
    });

    if (config.supportExpandableAllDayWell()) {
        let hiddenItems = cip.allDayHiddenItems;

        const transformItem = (item: SurfaceItem, rowIndex: number): JSX.Element => {
            const wrappedItem = transformAllDayItem(
                store,
                config.columnProvider,
                config.getItemConfig(item.model),
                item,
                rowIndex
            );
            let allowedResizeTypes = getAllowedResizeTypes(item);
            return makeDraggable(
                store,
                config.dragDropConfig,
                item,
                wrappedItem,
                allowedResizeTypes
            );
        };

        const getAllowedResizeTypes = (item: SurfaceItem): AllowedResizeTypes => {
            return getAllowedResizeTypesForAllDayItem(store, config.dragDropConfig, item.model);
        };

        result.push(
            ...generateOverflowItems(
                store,
                config,
                hiddenItems,
                transformItem,
                config.columnProvider,
                0 /* always only week in view */,
                cip.allDayRows.length,
                SurfaceConstants.AllDayRowHeight,
                SurfaceConstants.AllDayRowHeight * cip.allDayRows.length,
                0 // no additional padding,
            )
        );
    }

    if (store.enableDragDrop) {
        result.push(allDayDragHelperTemplate(store, config, cip.allDayRows, calendarFolderId));
    }

    return result;
}

function createCalendarTimeStripContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): JSX.Element[] {
    return [
        <TimeStripContent
            key={'timeStripContent'}
            surfaceStore={store}
            itemsLayout={config.itemsLayout}
            timeZones={store.timeZoneIds}
            timeStripConfig={config.timeStripConfig}
        />,
    ];
}

function createCalendarTimeStripHeaderContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): JSX.Element[] {
    return [
        <TimeStripHeaderContent
            key={'timeStripHeaderContent'}
            timeZones={store.timeZoneIds}
            timeStripConfig={config.timeStripConfig}
        />,
    ];
}

function createTimeMarkerContent(
    config: CalendarSurfaceTransformProps,
    customTimeMarkerClassName?: string
): JSX.Element[] {
    return [
        <TimeMarkerContent
            customMarkerStyles={customTimeMarkerClassName}
            columnProvider={config.columnProvider}
            key="timeMarker"
        />,
        <TimeMarkerTodayContent columnProvider={config.columnProvider} key="timeMarkerToday" />,
    ];
}
